import { useState, useEffect } from 'react';
import { getDeadline } from '../lib/posts';
import { Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import { chAiService } from '../services/aiService';
import { chPostService } from '../services/postService';
import { chProjectService } from '../services';
import { useChAuth } from '../contexts/ChAuthContext';

const CACHE_KEY = 'ch-daily-summary';
const CACHE_DATE_KEY = 'ch-daily-summary-date';

export function AiDailySummaryCard() {
  const { user } = useChAuth();
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const cachedDate = localStorage.getItem(CACHE_DATE_KEY);
    const cachedSummary = localStorage.getItem(CACHE_KEY);

    if (cachedDate === today && cachedSummary) {
      setSummary(cachedSummary);
    } else {
      generateSummary();
    }
  }, []);

  async function generateSummary() {
    if (!user) return;
    setLoading(true);
    setError('');

    try {
      const projects = await chProjectService.getAll();
      if (projects.length === 0) {
        setSummary('Sem projetos ativos.');
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      const digestData: Record<string, any> = {};

      for (const project of projects.slice(0, 5)) {
        const posts = await chPostService.getByProject(project.id);
        const awaitingReview = posts.filter(p => p.status === 'em_revisao');
        const changesRequested = posts.filter(p => p.client_decision === 'changes_requested');
        const overdue = posts.filter(p => {
          const deadline = getDeadline(p);
          return !!deadline && !deadline.met && deadline.daysLeft < 0;
        });

        if (awaitingReview.length > 0 || changesRequested.length > 0 || overdue.length > 0) {
          digestData[project.name] = {
            em_revisao: awaitingReview.length,
            alteracoes_pedidas: changesRequested.length,
            em_atraso: overdue.length,
            total_posts: posts.length,
          };
        }
      }

      if (Object.keys(digestData).length === 0) {
        const msg = 'Sem itens pendentes. Tudo em dia.';
        setSummary(msg);
        localStorage.setItem(CACHE_KEY, msg);
        localStorage.setItem(CACHE_DATE_KEY, today);
        return;
      }

      const firstProjectId = projects[0].id;
      const res = await chAiService.dailySummary({
        project_id: firstProjectId,
        digest_data: digestData,
      });

      if (res.success && res.data?.summary) {
        setSummary(res.data.summary);
        localStorage.setItem(CACHE_KEY, res.data.summary);
        localStorage.setItem(CACHE_DATE_KEY, today);
      } else {
        // Fallback to structured list
        const fallback = buildFallbackSummary(digestData);
        setSummary(fallback);
        localStorage.setItem(CACHE_KEY, fallback);
        localStorage.setItem(CACHE_DATE_KEY, today);
      }
    } catch {
      const cachedSummary = localStorage.getItem(CACHE_KEY);
      if (cachedSummary) {
        setSummary(cachedSummary);
      } else {
        setError('Não foi possível gerar o resumo.');
      }
    } finally {
      setLoading(false);
    }
  }

  function buildFallbackSummary(data: Record<string, any>): string {
    const lines: string[] = [];
    for (const [name, info] of Object.entries(data)) {
      const parts: string[] = [];
      if (info.em_revisao > 0) parts.push(`${info.em_revisao} em revisão`);
      if (info.alteracoes_pedidas > 0) parts.push(`${info.alteracoes_pedidas} com alterações pedidas`);
      if (info.em_atraso > 0) parts.push(`${info.em_atraso} em atraso`);
      if (parts.length > 0) lines.push(`${name}: ${parts.join(', ')}`);
    }
    return lines.join('\n') || 'Sem itens pendentes.';
  }

  function handleRefresh() {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_DATE_KEY);
    generateSummary();
  }

  if (!summary && !loading && !error) return null;

  return (
    <div className="ch-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[var(--ch-ink)] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          Resumo de hoje
        </h3>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          title="Atualizar"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-4">
          <RefreshCw className="w-4 h-4 animate-spin text-gray-300" />
          <span className="text-xs text-gray-400">A gerar resumo...</span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 text-xs text-red-600">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {summary && !loading && (
        <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{summary}</p>
      )}
    </div>
  );
}
